import { useCallback, useMemo, useState } from 'react';
import { Button } from '../../../components/primitives/Button';
import { FormInput } from '../../../components/primitives/FormInput';
import { Select } from '../../../components/primitives/Select';
import { Sheet } from '../../../components/primitives/Sheet';
import { VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useToast } from '../../../contexts/ToastContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { useUpdateVoiceCall } from '../../voice-agents/hooks';
import type {
  CallStructuredSummary,
  InterestLevel,
  UpdateVoiceCallDto,
  VoiceCall,
} from '../../voice-agents/types';

const INTEREST_LEVELS: InterestLevel[] = ['hot', 'warm', 'cold'];

const TEXT_FIELDS: (keyof Pick<
  CallStructuredSummary,
  | 'resumen'
  | 'dolor_principal'
  | 'necesidad'
  | 'presupuesto'
  | 'timeline_decision'
  | 'propuesta_sugerida'
  | 'proximos_pasos'
>)[] = [
  'resumen',
  'dolor_principal',
  'necesidad',
  'presupuesto',
  'timeline_decision',
  'propuesta_sugerida',
  'proximos_pasos',
];

type SummaryFormState = Record<(typeof TEXT_FIELDS)[number], string>;

interface EditCallSummarySheetProps {
  visible: boolean;
  call: VoiceCall;
  organizationId: string;
  onClose: () => void;
}

function initialTextState(summary: CallStructuredSummary | null | undefined): SummaryFormState {
  return TEXT_FIELDS.reduce((acc, key) => {
    acc[key] = summary?.[key] ?? '';
    return acc;
  }, {} as SummaryFormState);
}

function resolveInterest(call: VoiceCall): InterestLevel {
  return call.interestLevel ?? call.structuredSummary?.nivel_interes ?? 'warm';
}

function resolveProbability(call: VoiceCall): string {
  const value = call.closingProbability ?? call.structuredSummary?.probabilidad_cierre;
  return value === undefined || value === null ? '' : String(value);
}

export function EditCallSummarySheet({
  visible,
  call,
  organizationId,
  onClose,
}: Readonly<EditCallSummarySheetProps>) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const updateCall = useUpdateVoiceCall();

  const [fields, setFields] = useState<SummaryFormState>(() =>
    initialTextState(call.structuredSummary),
  );
  const [interest, setInterest] = useState<InterestLevel>(() => resolveInterest(call));
  const [probability, setProbability] = useState<string>(() => resolveProbability(call));
  const [probabilityError, setProbabilityError] = useState<string | null>(null);
  const [interestError, setInterestError] = useState<string | null>(null);

  const interestOptions = useMemo(
    () =>
      INTEREST_LEVELS.map((level) => ({
        value: level,
        label: t(`leads.callResults.interestLevel_${level}`),
      })),
    [t],
  );

  const setField = useCallback((key: (typeof TEXT_FIELDS)[number], value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleError = useCallback(
    (error: unknown) => {
      const httpStatus = (error as { response?: { status?: number } } | null)?.response?.status;
      if (httpStatus === 403) {
        showError(t('leads.callResults.edit.forbidden'));
        return;
      }
      showError(t('leads.callResults.edit.error'));
    },
    [showError, t],
  );

  const submit = useCallback(() => {
    let valid = true;

    if (!INTEREST_LEVELS.includes(interest)) {
      setInterestError(t('leads.callResults.edit.interestInvalid'));
      valid = false;
    } else {
      setInterestError(null);
    }

    const trimmedProbability = probability.trim();
    const parsedProbability = Number(trimmedProbability);
    const probabilityValid =
      trimmedProbability !== '' &&
      Number.isFinite(parsedProbability) &&
      Number.isInteger(parsedProbability) &&
      parsedProbability >= 0 &&
      parsedProbability <= 100;

    if (!probabilityValid) {
      setProbabilityError(t('leads.callResults.edit.probabilityInvalid'));
      valid = false;
    } else {
      setProbabilityError(null);
    }

    if (!valid) return;

    const structuredSummary: CallStructuredSummary = {
      resumen: fields.resumen,
      dolor_principal: fields.dolor_principal,
      necesidad: fields.necesidad,
      presupuesto: fields.presupuesto,
      timeline_decision: fields.timeline_decision,
      nivel_interes: interest,
      probabilidad_cierre: parsedProbability,
      propuesta_sugerida: fields.propuesta_sugerida,
      proximos_pasos: fields.proximos_pasos,
    };

    const dto: UpdateVoiceCallDto = {
      structuredSummary,
      interestLevel: interest,
      closingProbability: parsedProbability,
    };

    updateCall.mutate(
      { id: call.id, organizationId, leadId: call.leadId, dto },
      {
        onSuccess: () => {
          showSuccess(t('leads.callResults.edit.success'));
          onClose();
        },
        onError: handleError,
      },
    );
  }, [
    interest,
    probability,
    fields,
    updateCall,
    call.id,
    call.leadId,
    organizationId,
    showSuccess,
    handleError,
    onClose,
    t,
  ]);

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      title={t('leads.callResults.edit.title')}
      position="center"
      size="md"
    >
      <VStack spacing="lg">
        {TEXT_FIELDS.map((key) => (
          <VStack spacing="xs" key={key}>
            <Text variant="caption" color={colors.text.tertiary}>
              {t(`leads.callResults.fields.${key}`)}
            </Text>
            <FormInput
              value={fields[key]}
              onChangeText={(value) => setField(key, value)}
              multiline={key === 'resumen' || key === 'proximos_pasos'}
              accessibilityLabel={t(`leads.callResults.fields.${key}`)}
              testID={`edit-call-${key}`}
            />
          </VStack>
        ))}

        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.tertiary}>
            {t('leads.callResults.edit.interestLabel')}
          </Text>
          <Select
            variant="dropdown"
            options={interestOptions}
            value={interest}
            onChange={(value) => setInterest(value as InterestLevel)}
            testID="edit-call-interest"
          />
          {interestError ? (
            <Text variant="caption" color={colors.status.error.main}>
              {interestError}
            </Text>
          ) : null}
        </VStack>

        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.tertiary}>
            {t('leads.callResults.edit.probabilityLabel')}
          </Text>
          <FormInput
            value={probability}
            onChangeText={setProbability}
            keyboardType="number-pad"
            accessibilityLabel={t('leads.callResults.edit.probabilityLabel')}
            testID="edit-call-probability"
          />
          {probabilityError ? (
            <Text variant="caption" color={colors.status.error.main}>
              {probabilityError}
            </Text>
          ) : null}
        </VStack>

        <Button
          variant="primary"
          onPress={submit}
          accessibilityLabel={t('leads.callResults.edit.save')}
          accessibilityState={{ busy: updateCall.isPending }}
          disabled={updateCall.isPending}
          testID="edit-call-save"
        >
          {updateCall.isPending
            ? t('leads.callResults.edit.saving')
            : t('leads.callResults.edit.save')}
        </Button>
      </VStack>
    </Sheet>
  );
}
